import {HttpClient, HttpHeaders, HttpParams, HttpRequest} from "@angular/common/http";
import {Injectable} from '@angular/core';
import {AppUtils} from "@app/shared/util/app.utils";
import {TableConfig} from "@app/shared/util/table-config.utils";
import {BehaviorSubject} from "rxjs";
import {environment} from "../../../environments/environment";

import * as FileSaver from 'file-saver';
import {Tool} from "@app/tool/model/tool.model";
import {Group} from "@app/tool/model/group.model";
import {GroupChildWrapper} from "@app/tool/model/group-child-wrapper.model";
import {ToolDetails} from "@app/tool/model/toolDetails.model";
import {ToolDocuments} from "@app/tool/model/toolDocuments.model";
import {ToolIntervention} from "@app/tool/model/toolIntervention.model";
import {Subject} from "rxjs";
import {ToolDatamatrix} from "@app/tool/model/toolDatamatrix.model";
import {MaintenancePlan} from "@app/tool/model/toolMaintenance.model";
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn} from "@angular/forms";
import {ToolStorage} from "@app/tool/model/toolLocation.model";
import {ToolSet} from "@app/tool/model/toolSet.model";
import {ToolPosition} from '@app/tool/model/tool-position.model';
import {TrayType} from "@models/enums/tray-type.enum";
import {ToolReturnTypeEnum} from "@app/shared/models/enums/tool-return-type.enum";
import {ToolBulkResetRequest} from "@app/shared/models/tool-bulk-reset-request.model";
import {StrainMeasurement} from "@app/strain/common/models/strain-registry-measurement.model";
import {map} from "rxjs/operators";
import {ToolBulkUpdate} from "@app/tool/components/tool-batch-update-modal/models/tool-bulk-update.model";
import { ToolProperty } from "@app/tool/model/enums/tool-property.enum";
import { ToolPropertyValidationResult } from '@app/tool/model/tool-property-validation-result.model';


@Injectable()
export class ToolService {
    static readonly API_TOOL_BASE_PATH = `${environment.baseURL}/api/tools`;
    static readonly API_STRAIN_REPORTS_PATH = `${environment.baseURL}/api/strain/reports`;
    static readonly TOOL_API_DOWNLOAD_PATH = `${environment.baseURL}/api/tools/download`;

    private toolSelectedSource = new BehaviorSubject<Tool>(null);
    toolSelectedAsObservable = this.toolSelectedSource.asObservable();

    private toolUpdateSource = new BehaviorSubject<Tool>(null);
    toolUpdateObservable = this.toolUpdateSource.asObservable();

    reloadToolEvent = new Subject<any>();

    static allToolsAreScaned(tools: any) {
        let areScanned = true;
        tools.forEach((tool: Tool | ToolSet) => {
            if (tool instanceof Tool && !tool.isScanned) {
                areScanned = false;
                return false;
            } else if (tool instanceof ToolSet) {
                tool.tools.forEach((toolIsSet: Tool) => {
                    if (!toolIsSet.isScanned) {
                        areScanned = false;
                        return false;
                    }
                })
            }
        });
        return areScanned;
    }

    static mapToolsAndSets(items) {
        return items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
    }


    constructor(private http: HttpClient) {
    }

    getTools(tableConfig: TableConfig = null, includeDeleted = false, includeSecondaryLines = false) {
        let params = AppUtils.buildPageableParams(tableConfig);
        params = params.append('includeDeleted', '' + includeDeleted)
            .append('includeSecondaryLines', '' + includeSecondaryLines);
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}`, {
            params: params
        }).pipe(map(this.mapGroups));
    }

    getToolsForSlot(slotId: number) {
        let params = new HttpParams();
        params = params.set('slotId', slotId.toString());
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}`, {
            params: params
        }).pipe(map(this.mapGroups));
    }

    getToolsByMainLines(lines: number[], tableConfig: TableConfig = null) {
        const pageableParams = AppUtils.buildPageableParams(tableConfig);
        const params = new HttpParams({
            fromObject: {
                "lineIds": lines.map(id => id.toString()),
                "page": pageableParams.get("page"),
                "pageSize": pageableParams.get("pageSize"),
                "name": pageableParams.get("name") || '',
                "toolUid": pageableParams.get("toolUid") || '',
                "line": pageableParams.get("line") || '',
                "sortBy": pageableParams.get("sortBy") || '',
                "IsSortingAscending": pageableParams.get("IsSortingAscending") || '',
            }
        });

        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}`, {
            params: params
        }).pipe(map(this.mapGroups));
    }

    getToolsByPartNumber(partNumberId: number) {
        const params = new HttpParams().append("partNumberId", partNumberId.toString());
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}`, {
            params: params
        }).pipe(map(this.mapGroups));
    }

    getToolsByIds(ids: number[]) {
        let params = new HttpParams();
        ids.forEach(id => params = params.append('ids', id.toString()));
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/groups`, {params: params});
    }

    getToolsForLineAndPartNumber(tableConfig: TableConfig = null, lineId: number, partNumberId: number,
                                 includeSecondaryLines = false, insertionCycleId: number = null) {

        const partNumberFilterIndex = tableConfig.filters.findIndex(x => x.field === 'partNumberId');
        const equipmentFilterIndex = tableConfig.filters.findIndex(x => x.field === 'equipmentId');
        if (tableConfig.filters[partNumberFilterIndex] && tableConfig.filters[partNumberFilterIndex].value === 0) {
            tableConfig.filters.splice(partNumberFilterIndex, 1);
        }
        if (tableConfig.filters[equipmentFilterIndex] && tableConfig.filters[equipmentFilterIndex].value === 0) {
            tableConfig.filters.splice(equipmentFilterIndex, 1);
        }

        let params = AppUtils.buildPageableParams(tableConfig);
        if (lineId) {
            params = params.set('lineId', lineId.toString());
        }
        if (partNumberId) {
            params = params.set('partNumberId', partNumberId.toString());
        }
        if (insertionCycleId) {
            params = params.set('insertionCycleId', insertionCycleId.toString())
        }


        params = params.append('isSpare', 'false')
            .append('includeSecondaryLines', '' + includeSecondaryLines);

        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}`, {
            params: params
        }).pipe(map(this.mapGroups));
    }

    setMaintenanceType(toolId: number, maintenanceType: string) {
        return this.http.patch(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/`, {maintenancePlanType: maintenanceType});
    }

    reloadTools() {
        this.reloadToolEvent.next();
    }

    onReloadTools() {
        return this.reloadToolEvent.asObservable();
    }

    getToolById(id: number) {
        return this.http.get<Tool>(`${ToolService.API_TOOL_BASE_PATH}/${id}`)
            .pipe(map(this.toTool));
    }

    getDetailsForTool(id: number) {
        return this.http.get<ToolDetails>(`${ToolService.API_TOOL_BASE_PATH}/${id}/details`)
            .pipe(map(this.toToolDetails));
    }

    getToolsThatNeedLineSwitch(ids: number[], lineId: any) {
        return this.http.post<any[]>(`${ToolService.API_TOOL_BASE_PATH}/getToolsThatNeedLineSwitch`, {lineId: lineId, toolIds: ids});
    }

    getDocumentationForTool(id: number) {
        return this.http.get<ToolDocuments>(`${ToolService.API_TOOL_BASE_PATH}/${id}/documents`)
            .pipe(map(this.toToolDocuments));
    }

    getRemovedDocuments(plantId: number, newPath: string) {
        let params = new HttpParams();
        params = params.append('plantId', plantId.toString());
        params = params.append('newPath', newPath);
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/documents/obsolete`, {params: params});
    }

    getDocumentsLocationForTool(id: number, type: string) {
        let params = new HttpParams();
        params = params.append('type', type.toString());
        return this.http.get<ToolDocuments>(`${ToolService.API_TOOL_BASE_PATH}/${id}/documents/location`, {params: params})
    }

    updateDocumentsLocationForTool(id: number, paths: string[], type: string) {
        return this.http.post<ToolDocuments>(`${ToolService.API_TOOL_BASE_PATH}/${id}/documents/location`, {paths: paths, type: type})
    }

    getToolDocumentTypes() {
        return this.http.get<any[]>(`${ToolService.API_TOOL_BASE_PATH}/documents/types`);
    }

    downloadFile(path: any) {
        let params = new HttpParams();
        params = params.append('path', path.toString());
        const req = new HttpRequest('GET', `${ToolService.API_TOOL_BASE_PATH}/documents/download`,
            null, {responseType: 'blob', params: params});
        this.http.request(req).subscribe(
            (data: any) => {
                if (data.body != null) {
                    const filename = path.split("\\").pop();
                    FileSaver.saveAs(data.body, filename);
                }
            });
    }

    getInterventionsForTool(id: number, tableConfig: TableConfig) {
        const params = AppUtils.buildPageableParams(tableConfig);
        return this.http.get<ToolIntervention[]>(`${ToolService.API_TOOL_BASE_PATH}/${id}/interventions`, {
            params: params
        }).pipe(map(this.toToolInterventions));
    }

    getToolMaintenancePlan(id: number, tableConfig: TableConfig) {
        const params = AppUtils.buildPageableParams(tableConfig);
        return this.http.get<ToolIntervention[]>(`${ToolService.API_TOOL_BASE_PATH}/${id}/maintenancePlan`, {
            params: params
        }).pipe(map(this.toToolMaintenancePlan));
    }

    addMaintenancePlan(id: number, maintenancePlan: any) {
        return this.http.post(`${ToolService.API_TOOL_BASE_PATH}/${id}/maintenancePlan`, maintenancePlan);
    }

    deleteMaintenancePlan(toolId: number, maintenancePlanId) {
        return this.http.delete(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/maintenancePlan/${maintenancePlanId}`);
    }

    getToolSetsByToolUID(uid: string, tableConfig: TableConfig) {
        const params = AppUtils.buildPageableParams(tableConfig);
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/${uid}/set`, {params})
            .pipe(map(this.mapSets));
    }

    updateTool(tool) {
        return this.http.patch<Tool>(`${ToolService.API_TOOL_BASE_PATH}/${tool.id}`, tool)
            .pipe(map(this.toTool));
    }

    updateToolsInBulk(bulk: ToolBulkUpdate) {
        const tools = this.extractToolIds(bulk.tools);
        return this.http.patch<Tool>(`${ToolService.API_TOOL_BASE_PATH}/bulk-update`, {
            ownerId: bulk.changes?.owner?.id,
            toolAvailability: bulk.changes?.toolAvailability,
            toolAvailabilityReason: bulk.changes?.toolAvailabilityReason,
            toolIds: tools
        });
    }

    updateToolValidation(tool, boschId, reason) {
        const headers = new HttpHeaders().set("BoschId", boschId);
        return this.http.patch<Tool>(`${ToolService.API_TOOL_BASE_PATH}/${tool.id}/validate`, {reason: reason}, {headers: headers});
    }

    /***
     *  #HACKERMAN - http.delete does not support a body so voila, http.request instead
     */
    deleteTool(deleteConfirmation: any) {
        const params = new HttpParams().set('isSet', deleteConfirmation.isSet);
        return this.http.request('delete', `${ToolService.API_TOOL_BASE_PATH}/${deleteConfirmation.toolId}`,
            {
                params: params,
                body: {
                    deleteReason: deleteConfirmation.deleteReason
                }
            },
        );
    }

    resetTool(tool) {
        return this.http.patch(`${ToolService.API_TOOL_BASE_PATH}/${tool.id}/storeInLift`, null);
    }

    resetToolsBulk(payload: ToolBulkResetRequest) {
        return this.http.patch(`${ToolService.API_TOOL_BASE_PATH}/bulk-reset`, payload);
    }

    generateToolAcceptanceReport(toolIds: number[]) {
        return this.http.post(`${ToolService.API_TOOL_BASE_PATH}/acceptance-report`, toolIds, {
            observe: 'response',
            responseType: 'blob'
        });
    }

    isInUse(tool) {
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/${tool.id}/isInUse`, tool).pipe(map(this.toolInUse));
    }

    areToolsInUseBulk(toolBulkInUse: any) {
        return this.http.post<boolean>(
            `${ToolService.API_TOOL_BASE_PATH}/bulk-inuse`,
            toolBulkInUse
        );
    }

    announceToolSelection(tool: Tool) {
        this.toolSelectedSource.next(tool);
    }

    announceToolUpdate(tool: Tool) {
        this.toolUpdateSource.next(tool);
    }

    createTool(tool: any) {
        return this.http.post<any>(`${ToolService.API_TOOL_BASE_PATH}`, tool);
    }

    // Adding tool into a set
    addToolIntoSet(toolUID, toolData) {
        return this.http.post<any>(`${ToolService.API_TOOL_BASE_PATH}/${toolUID}/addToSet`, toolData);
    }

    getToolDatamatrix(toolId) {
        return this.http.get<ToolDatamatrix[]>(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/dataMatrix`)
            .pipe(map(items => items.map(toolDm => new ToolDatamatrix(toolDm))));
    }

    getToolBackups(toolId) {
        return this.http.get<Tool[]>(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/backups`)
            .pipe(map(response => response.map(backup => new Tool(backup))));
    }

    confirmToolEngrave(engravedTool: ToolDatamatrix) {
        return this.http.patch<ToolDatamatrix>(`${ToolService.API_TOOL_BASE_PATH}/${engravedTool.toolId}/confirmEngraved`, {
            engravedData: engravedTool.engravedCode
        });
    }

    saveToolDocument(toolId, type, toolDocument: any) {
        return this.http.put(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/documents?type=${type}`, toolDocument);
    }

    downloadToolReport(requestConfig: any = null) {
        let params = AppUtils.buildPageableParams(requestConfig);
        params = params.set('includeDeleted', '' + true);
        return this.http.get(ToolService.TOOL_API_DOWNLOAD_PATH,
            {observe: 'response', params: params, responseType: 'blob'});
    }

    buildLocationFormGroup(locations: ToolStorage[]) {
        const group: any = {};

        locations.forEach((location: ToolStorage) => {
            group[location.key] = new UntypedFormControl(location, [this.allLocationFields()])
        });

        return new UntypedFormGroup(group);
    }

    allLocationFields(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {
            const valid = control.value &&
                control.value.liftNumber &&
                control.value.liftShelf > 0 &&
                control.value.shelfPosition &&
                control.value.cart > 0;
            return valid ? null : {'allLocationFields': {value: control.value}};
        };
    }

    regenerateDataMatrix(toolToReset: ToolDatamatrix, resetData: any) {
        return this.http.post(`${ToolService.API_TOOL_BASE_PATH}/${toolToReset.toolId}/dataMatrix/regenerate`,
            {reason: resetData.reason}, {
                headers: new HttpHeaders({
                    'BoschId': resetData.boschId
                })
            });
    }

    getToolLiftPositions(toolId: number, trayType: TrayType) {
        let params = new HttpParams();
        params = params.set('type', trayType.toString());
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/positions`, {params})
            .pipe(map(this.mapPositions));

    }

    downloadToolInterventionReport(id: number, requestConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(requestConfig);
        return this.http.get(`${ToolService.API_TOOL_BASE_PATH}/${id}/interventions/download`,
            {observe: 'response', params: params, responseType: 'blob'});
    }

    downloadStrainReport(reportId: any) {
        return this.http.get(`${ToolService.API_STRAIN_REPORTS_PATH}/${reportId}/download`,
            {observe: 'response', responseType: 'blob'});
    }

    getLastToolRequestById(id: number, type?: ToolReturnTypeEnum) {
        let params = new HttpParams();
        if (type) {
            params = params.set('type', type.toString());
        }
        return this.http.get<Object>(`${ToolService.API_TOOL_BASE_PATH}/${id}/requests`, {params});
    }

    getLastToolReturnById(id: number, type?: ToolReturnTypeEnum) {
        let params = new HttpParams();
        if (type) {
            params = params.set('type', type.toString());
        }
        return this.http.get<Object>(`${ToolService.API_TOOL_BASE_PATH}/${id}/returns`, {params});
    }

    getStrainMeasurementsForTool(toolId: number) {
        return this.http.get<Object>(`${ToolService.API_TOOL_BASE_PATH}/${toolId}/strain-measurements`)
            .pipe(map(this.mapMeasurements));
    }

    validateToolProperty(
        property: ToolProperty,
        value: string,
        options?: number | {
            lineId?: number,
            codingType?: number | string,
            equipmentIds?: number[],
            toolId?: number
        }
    ) {
        const body: any = { property, value };

        if (typeof options === 'number') {
            body.lineId = options;
        } else if (options) {
            if (options.lineId) {
                body.lineId = options.lineId;
            }
            if (options.codingType) {
                body.codingType = options.codingType;
            }
            if (options.equipmentIds?.length) {
                body.equipmentIds = options.equipmentIds;
            }
            if (options.toolId) {
                body.toolId = options.toolId;
            }
        }

        return this.http.post<ToolPropertyValidationResult>(
            `${ToolService.API_TOOL_BASE_PATH}/validate-property`,
            body
        );
    }

    // TODO #CHALLENGE5 For some arcane reason, the Redux Devtools crashes if tools are mapped this way. Why?
    private mapGroups = res => {
        const body = res;
        return {
            tools: this.mapGroupableItems(body.items),
            count: body.total,
            totalCount: body.totalTools
        };
    };

    mapGroupableItems(items) {
        return items.map(group => {
            if (group.subGroup) {
                return new Group(group);
            }
            return new GroupChildWrapper(group).item;
        });
    }

    private mapSets = response => {
        return {
            items: response,
            total: response.length
        }
    };

    private mapPositions = response => {
        return response.map(position => new ToolPosition(position));
    };

    private toTool(response) {
        return new Tool(response);
    }

    private toToolDetails(response) {
        return new ToolDetails(response);
    }

    private toToolDocuments(response) {
        return new ToolDocuments(response);
    }

    private toToolInterventions(response) {
        return {
            items: response.items.map(item => new ToolIntervention(item)),
            total: response.total
        };
    }

    private toToolMaintenancePlan(response) {
        return {
            items: response.toolMaintenancePlantItems.items.map(item => new MaintenancePlan(item)),
            total: response.toolMaintenancePlantItems.total,
            type: response.type
        }
    }

    private toolInUse(response) {
        return {
            toolRequest: response.toolRequest,
            toolReturn: response.toolReturn,
            maintenanceReturn: response.maintenanceReturn,
            ticket: response.ticket,
            ticketReturn: response.ticketReturn,
            ticketRequest: response.ticketRequeest,
        }
    }

    private mapMeasurements(measurements: any) {
        return measurements.map(item => new StrainMeasurement(item));
    }

    private extractToolIds(selectedTools: Array<ToolSet | Tool>): Array<number> {
        const toolIds: number[] = [];
        selectedTools.forEach(item => {
            if (item instanceof ToolSet) {
                const setIds = item.tools.map(tool => tool.id);
                toolIds.push(...setIds);
            } else {
                toolIds.push(item.id);
            }
        });
        return toolIds;
    }

}
